/**
 * The questions a feedback request asks (HR UAT #5 and #13, Oct 2026).
 *
 * The request dialog starts from a template, and the requester may change,
 * remove and add questions. Both response pages used to replace that list
 * with the template's current questions whenever the request had a template,
 * so every edit was lost. The dialog now stores the requester's final list,
 * each question marked with its `source`, and a list that carries the marker
 * is what respondents answer. Requests without it (older requests and
 * campaigns) keep answering the template exactly as before.
 *
 * Template questions keep their question bank id as their id, so answers
 * still group by question in the campaign and synthesis reports. A template
 * question the requester rewords becomes their own question instead, so the
 * reports never pool answers to two different wordings (see rewordQuestion).
 */
import type { FeedbackQuestion, FeedbackRequest } from '@/services/api/feedbackService'
import {
  questionCategoryLabel,
  type FeedbackTemplate,
} from '@/services/api/questionBankService'

type RequestType = 'MATRIX' | 'EMPLOYEE_REQUESTED' | 'UPWARD_360' | 'INFORMATION_CHECKIN' | 'PROJECT'

/** Questions used when a context has no template in the question bank. */
const DEFAULT_QUESTIONS: Record<RequestType, string[]> = {
  MATRIX: [
    'What are the key strengths demonstrated in the current role?',
    'What areas would benefit from further development?',
    'How effectively does this person collaborate with stakeholders?',
    'What impact has this person had on their team/department?'
  ],
  EMPLOYEE_REQUESTED: [
    'What are my key strengths that I should continue to build on?',
    'What areas should I focus on for my professional development?',
    'How can I improve my collaboration with you?'
  ],
  UPWARD_360: [
    'How effectively does this leader communicate vision and expectations?',
    'How well does this leader support team development?',
    'What leadership strengths have you observed?',
    'What areas could this leader improve?'
  ],
  INFORMATION_CHECKIN: [
    'Quick feedback on recent collaboration?',
    'Any immediate suggestions or support needed?'
  ],
  PROJECT: [
    'Were you able to complete this step successfully?',
    'Did you encounter any issues or blockers?',
    'How would you rate the ease of use?',
    'Any suggestions for improvement?'
  ]
}

/** Whether a question list is a requester's final list (it carries a source). */
export function isQuestionSnapshot(questions?: FeedbackQuestion[] | null): boolean {
  return Array.isArray(questions) && questions.some((q) => q.source === 'template' || q.source === 'custom')
}

/**
 * The parts of a template the response pages read: the full template in the
 * app, or the public link's trimmed copy (AnonymousFeedbackRequest).
 */
interface TemplateQuestionsLike {
  template_questions?: {
    display_order: number
    is_required: boolean
    custom_question_text?: string
    custom_helper_text?: string
    question: { id: string; question_text: string; question_type: string; helper_text?: string; question_config?: object }
  }[]
}

/**
 * A template's questions as the response pages have always mapped them, for
 * requests without a final list. Kept exactly as it was: campaigns answer
 * through this, and their surveys must not change.
 */
export function templateQuestions(template: TemplateQuestionsLike): FeedbackQuestion[] {
  return [...(template.template_questions || [])]
    .sort((a, b) => a.display_order - b.display_order)
    .map((tq) => ({
      id: tq.question.id,
      text: tq.custom_question_text || tq.question.question_text,
      type: tq.question.question_type.toLowerCase() as FeedbackQuestion['type'],
      required: tq.is_required,
      help_text: tq.custom_helper_text || tq.question.helper_text,
      // Pass config/scale for rating questions
      config: tq.question.question_config as FeedbackQuestion['config'],
      scale: tq.question.question_config as FeedbackQuestion['scale'], // Also set as scale for compatibility
    }))
}

/**
 * The questions a respondent answers: the requester's final list when the
 * request carries one, otherwise the template's questions, otherwise the
 * request's own questions.
 */
export function questionsToAnswer(
  request: Pick<FeedbackRequest, 'custom_questions'> & { question_template?: TemplateQuestionsLike | null },
): FeedbackQuestion[] {
  if (isQuestionSnapshot(request.custom_questions)) return request.custom_questions!
  if (request.question_template) return templateQuestions(request.question_template)
  return request.custom_questions || []
}

/**
 * A template's questions as the start of a requester's final list: each keeps
 * its type, scale or options, required flag, category and question bank id.
 */
export function snapshotFromTemplate(template: FeedbackTemplate): FeedbackQuestion[] {
  const usedIds = new Set<string>()
  return [...(template.template_questions || [])]
    .sort((a, b) => a.display_order - b.display_order)
    .filter((tq) => !!(tq.custom_question_text || tq.question?.question_text))
    .map((tq) => {
      const bankId = tq.question?.id || tq.question_id
      // Answers are keyed by id, so a bank question linked twice needs a
      // second id; its bank id still groups the answers.
      let id = bankId
      for (let n = 2; usedIds.has(id); n++) id = `${bankId}-${n}`
      usedIds.add(id)
      const config = tq.question?.question_config || undefined
      return {
        id,
        text: (tq.custom_question_text || tq.question.question_text).trim(),
        type: String(tq.question?.question_type || 'text').toLowerCase() as FeedbackQuestion['type'],
        required: tq.is_required,
        help_text: tq.custom_helper_text || tq.question?.helper_text || undefined,
        config: config as FeedbackQuestion['config'],
        scale: config as FeedbackQuestion['scale'],
        category: tq.question?.category ? String(tq.question.category).toLowerCase() : undefined,
        source: 'template',
        bank_question_id: bankId,
      }
    })
}

/** A question the requester wrote: a required written answer. */
export function customQuestion(text: string, existing: FeedbackQuestion[]): FeedbackQuestion {
  const taken = new Set(existing.map((q) => q.id))
  let n = existing.filter((q) => q.source === 'custom').length + 1
  while (taken.has(`custom-${n}`)) n++
  return { id: `custom-${n}`, text: text.trim(), type: 'text', required: true, source: 'custom' }
}

/** Whether two wordings are the same once spacing is ignored. */
function sameWording(a: string, b: string): boolean {
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim()
  return norm(a) === norm(b)
}

/**
 * The list with question `index` reworded to `text`.
 *
 * A template question whose wording changes becomes the requester's own
 * question (Victor, Oct 2026): it loses its question bank id, category and
 * helper text, and takes an id of its own, so reports never pool answers to
 * two different wordings. It keeps how it is answered (type, scale or
 * options, required). Unchanged wording, spacing aside, changes nothing, so
 * the question keeps its bank id.
 */
export function rewordQuestion(list: FeedbackQuestion[], index: number, text: string): FeedbackQuestion[] {
  const question = list[index]
  const wording = text.trim()
  if (!question || !wording || sameWording(wording, question.text)) return list
  if (question.source !== 'template') {
    return list.map((q, i) => (i === index ? { ...q, text: wording } : q))
  }
  const own: FeedbackQuestion = {
    ...customQuestion(wording, list),
    type: question.type,
    required: question.required,
    ...(question.options ? { options: question.options } : {}),
    ...(question.config ? { config: question.config } : {}),
    ...(question.scale ? { scale: question.scale } : {}),
  }
  return list.map((q, i) => (i === index ? own : q))
}

/** The built-in questions for a request type, for contexts without a template. */
export function defaultQuestions(requestType: RequestType): FeedbackQuestion[] {
  return (DEFAULT_QUESTIONS[requestType] || []).reduce<FeedbackQuestion[]>(
    (list, text) => [...list, customQuestion(text, list)],
    [],
  )
}

export interface QuestionSection {
  key: string
  /** Heading shown above the section; null for an unheaded list. */
  title: string | null
  questions: FeedbackQuestion[]
}

/** Heading for the questions the requester wrote themselves. */
export const CUSTOM_SECTION_TITLE = 'Additional questions'

/**
 * A final list grouped under a heading per question bank category, in the
 * order the categories first appear, with the requester's own questions
 * under their own heading at the end. Lists without the marker stay one
 * unheaded list, as before; so does a list of only the requester's own
 * questions, which a lone heading would not help.
 */
export function questionSections(questions: FeedbackQuestion[]): QuestionSection[] {
  if (!isQuestionSnapshot(questions)) return [{ key: 'all', title: null, questions }]
  const custom: FeedbackQuestion[] = []
  const byCategory = new Map<string, FeedbackQuestion[]>()
  for (const q of questions) {
    if (q.source === 'custom' || !q.category) {
      custom.push(q)
      continue
    }
    const key = q.category.toLowerCase()
    byCategory.set(key, [...(byCategory.get(key) || []), q])
  }
  if (byCategory.size === 0) return [{ key: 'all', title: null, questions }]
  const sections: QuestionSection[] = Array.from(byCategory, ([key, list]) => ({
    key,
    title: questionCategoryLabel(key),
    questions: list,
  }))
  if (custom.length) sections.push({ key: 'custom', title: CUSTOM_SECTION_TITLE, questions: custom })
  return sections
}

/** Short description of how a question is answered, for the request dialog. */
export function answerTypeLabel(question: FeedbackQuestion): string {
  const cfg = question.config || question.scale
  switch (question.type) {
    case 'rating':
    case 'rating_scale':
    case 'scale':
      return `Rating ${cfg?.min ?? 1}–${cfg?.max ?? 5}`
    case 'likert':
      return 'Agreement scale'
    case 'multiple_choice':
      return 'Pick one'
    case 'multi_select':
      return 'Pick any'
    case 'yes_no':
      return 'Yes or no'
    default:
      return 'Written answer'
  }
}
